import { Component, ViewChild } from '@angular/core';
import { Output, Input, EventEmitter } from '@angular/core';
import { DragAndDropComponent } from '../../dispatch/drag-and-drop/drag-and-drop.component';
import { DataServiceService } from '../../services/data-service.service';
import { Customers, DriverCard } from '../../interfaces';
import { Subscription } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';


@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss'
})


export class ToolbarComponent {

  constructor(private dataServiceService: DataServiceService) {
    this.dataServiceService.driverCards$.subscribe(drivers => {
      this.driverCards = drivers;
    });
  }

  // Access to drag and drop component
  @ViewChild('v2dragAndDrop') dragAndDropComponent!: DragAndDropComponent;

  @Input() sidebarVisible: boolean = false;

  @Output() sidebarStateChange = new EventEmitter<boolean>();
  @Output() setSelectedDriver = new EventEmitter<DriverCard[]>();
  @Output() orderAssigned = new EventEmitter<any>();
  @Output() orderUnassigned = new EventEmitter<void>();
  @Output() onStatusChanged = new EventEmitter<DriverCard[]>();


  driverCards: DriverCard[] = [];   // object to hold drivercards
  selectedDriver: any = null;       // driver selected from autocomplete options
  filteredDrivers: any[] = [];      // drivers shown in autcomplete based on query in search bar
  selectedStatus: string | null = null; // selected availability status for filtering
  selectedDate: string = '';        // empty date string to receive the selected date from header

  statusOptions = [
    { label: 'All Drivers', value: 'all' },
    { label: 'Available', value: 'available' },
    { label: 'Unavailable', value: 'unavailable' }
  ];

  private subscriptions: Subscription = new Subscription();

  ngOnInit() {
    // Subscribe to selectedDate
    this.subscriptions.add(
      this.dataServiceService.selectedDate$.subscribe((date) => {
        this.selectedDate = date;
      })
    );

    this.dataServiceService.driverCards$.subscribe(cards => {
      this.driverCards = cards;
      if (this.selectedStatus && this.selectedStatus !== 'all') {
        this.filteredDrivers = this.driverCards.filter(card =>
          card.dailyDispatch.daily_driver_status.toLowerCase() === this.selectedStatus?.toLowerCase()
        );
      } else {
        this.filteredDrivers = [...this.driverCards];
      }
      this.setSelectedDriver.emit(this.filteredDrivers);
    });
  }

  onSelect(event: any) {
    const selectedItem = event.value;

    if (selectedItem.type === 'pickup') {
      const pickupNumber = selectedItem.data.order_id.toString();

      this.dataServiceService.getDailyDispatch(this.selectedDate, {
        pickup_number: pickupNumber
      }).subscribe({
        next: (data: DriverCard[]) => {
          this.filteredDrivers = data.map((card) => ({
            label: `${card.driver.first_name} ${card.driver.last_name}`,
            type: 'driver',
            data: card
          }));

          if (data.length > 0) {
            this.selectedDriver = this.filteredDrivers[0];
            this.setSelectedDriver.emit(data);
          }
        },
        error: (error) => console.error('Error fetching driver by pickup:', error)
      });

    } else if (selectedItem.type === 'driver') {
      this.selectedDriver = selectedItem;
      this.setSelectedDriver.emit([selectedItem.data]);
      this.filteredDrivers = [selectedItem];

    } else if (selectedItem.type === 'receiver') {
      this.selectedDriver = selectedItem;
      this.setSelectedDriver.emit(selectedItem.data);
      this.filteredDrivers = [selectedItem];
    }
  }


  onClear() {
    this.selectedDriver = null;
    this.filteredDrivers = [];
    this.setSelectedDriver.emit([]);
  }


  filterDriver(event: { query: string }) {
    const query = event.query.toLowerCase().trim();

    if (!query) {
      this.filteredDrivers = [];
      return;
    }

    const isPickupNumber = /^\d+$/.test(query);

    this.dataServiceService.getDailyDispatch(this.selectedDate).pipe(
      withLatestFrom(this.dataServiceService.customers$)
    ).subscribe({
      next: ([data, customers]: [DriverCard[], Customers[]]) => {
        const suggestions: any[] = [];

        if (isPickupNumber) {
          // Match by pickup number
          data.forEach((card) => {
            const matchingOrder = card.dispatchOrders?.find(order =>
              order.pickup_number?.toLowerCase().includes(query)
            );
            if (matchingOrder) {
              suggestions.push({
                label: `${card.driver.first_name} ${card.driver.last_name} - Pickup #: ${matchingOrder.pickup_number}`,
                type: 'driver',
                data: card,
              });
            }
          });
        } else {
          // Match by driver name
          data.forEach((card) => {
            const fullName = `${card.driver.first_name} ${card.driver.last_name}`.toLowerCase();
            const matchesDriver =
              card.driver.first_name.toLowerCase().includes(query) ||
              card.driver.last_name.toLowerCase().includes(query) ||
              fullName.includes(query);
            if (matchesDriver) {
              suggestions.push({
                label: `${card.driver.first_name} ${card.driver.last_name}`,
                type: 'driver',
                data: card,
              });
            }
          });

          // Match by receiver name — one suggestion per receiver, carrying all matching drivers
          const matchingReceivers = customers.filter(c =>
            c.name.toLowerCase().includes(query)
          );

          if (matchingReceivers.length > 0) {
            const alreadyIncluded = new Set(suggestions.map(s => s.data.driver.driver_id));

            matchingReceivers.forEach((receiver) => {
              const driversForReceiver = data.filter(card =>
                card.dispatchOrders?.some(order => order.receiver_id === receiver.customer_id) &&
                !alreadyIncluded.has(card.driver.driver_id)
              );

              if (driversForReceiver.length > 0) {
                suggestions.push({
                  label: `Receiver: ${receiver.name} (${driversForReceiver.length} driver${driversForReceiver.length > 1 ? 's' : ''})`,
                  type: 'receiver',
                  data: driversForReceiver,
                });
              }
            });
          }
        }

        this.filteredDrivers = suggestions;
      },
      error: (error) => console.error('Error fetching drivers:', error),
    });
  }

  onEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const query = (event.target as HTMLInputElement).value.trim().toLowerCase();

      if (!query) {
        this.filteredDrivers = [];
        return;
      }

      const isPickupNumber = /^\d+$/.test(query);

      const params = isPickupNumber
        ? { pickup_number: query }
        : { driver_first_name: query, driver_last_name: query };

      this.dataServiceService.getDailyDispatch(this.selectedDate, params).subscribe({
        next: (data: DriverCard[]) => {
          const filteredResults = data.map((card) => {
            const fullName = `${card.driver.first_name} ${card.driver.last_name}`.toLowerCase();
            const similarityScore = this.calculateSimilarity(query, fullName);

            return {
              card,
              similarityScore,
            };
          });

          // Sort results by similarity score
          filteredResults.sort((a, b) => b.similarityScore - a.similarityScore);

          // Auto-select the closest match (if available)
          if (this.filteredDrivers.length > 0) {
            this.selectedDriver = this.filteredDrivers[0];
            this.setSelectedDriver.emit([this.filteredDrivers[0].data]);
          }
        },
        error: (error) => console.error('Error fetching drivers:', error),
      });
    }
  }

  calculateSimilarity(query: string, fullName: string): number {
    const queryParts = query.split(' '); // Split query into words
    let score = 0;

    for (const part of queryParts) {
      if (fullName.includes(part)) {
        score += part.length; // Higher score for longer matches
      }
    }
    return score; // Higher scores indicate better matches
  }

  onFilter(event: any) {
    const status = event.value.toLowerCase();
    this.selectedStatus = status; // Track the selected status

    this.dataServiceService.getDailyDispatch(this.selectedDate).subscribe({
      next: (data) => {
        this.driverCards = data;
        if (status === 'all') {
          this.filteredDrivers = [...data];
        } else {
          this.filteredDrivers = data.filter(card =>
            card.dailyDispatch.daily_driver_status.toLowerCase() === status
          );
        }
        this.onStatusChanged.emit(this.filteredDrivers);
      },
      error: (error) => {
        console.error('Error filtering drivers:', error);
        this.filteredDrivers = [];
        this.onStatusChanged.emit(this.filteredDrivers);
      }
    });
  }

  onOrderAssigned(order: any) {
    this.orderAssigned.emit(order);
  }

  onOrderUnassigned() {
    this.orderUnassigned.emit();
  }

  onSidebarHide() {
    this.sidebarVisible = false;
    this.sidebarStateChange.emit(false);
  }

  onSidebarChange() {
    this.sidebarStateChange.emit(this.sidebarVisible);
  }
}